import { z } from 'zod';

/**
 * Portal do Fornecedor (ADR 0072): acessos de pessoas de fora, convites e revogação. Usado pela
 * API e pelas telas internas; as telas do portal entram na fase P0-B.
 */

export const PORTAL_LANGUAGES = ['pt-BR', 'en'] as const;
export type PortalLanguage = (typeof PORTAL_LANGUAGES)[number];

export const PORTAL_ACCOUNT_STATUSES = ['invited', 'active', 'suspended', 'revoked'] as const;
export type PortalAccountStatus = (typeof PORTAL_ACCOUNT_STATUSES)[number];

export const PORTAL_ACCESS_STATUSES = ['active', 'revoked'] as const;
export type PortalAccessStatus = (typeof PORTAL_ACCESS_STATUSES)[number];

/** Validade do convite (decisão Q3 da fase P0). */
export const PORTAL_INVITE_DAYS = 7;

/** Idioma do primeiro acesso: português para fornecedor do Brasil, inglês para os demais. */
export function portalLanguageFor(country: string): PortalLanguage {
  return country === 'BR' ? 'pt-BR' : 'en';
}

/** Quando vence um convite criado agora. */
export function portalInviteExpiry(from: Date = new Date()): Date {
  return new Date(from.getTime() + PORTAL_INVITE_DAYS * 86_400_000);
}

/**
 * Situação que a tela mostra para um acesso: revogado, convite vencido, aguardando o primeiro
 * acesso ou ativo.
 */
export type PortalAccessView = 'revoked' | 'invite_expired' | 'invited' | 'active';

export function portalAccessView(a: {
  status: PortalAccessStatus;
  acceptedAt: Date | string | null;
  openInviteExpiresAt: Date | string | null;
  now?: Date;
}): PortalAccessView {
  if (a.status === 'revoked') return 'revoked';
  if (a.acceptedAt) return 'active';
  const now = a.now ?? new Date();
  if (!a.openInviteExpiresAt || new Date(a.openInviteExpiresAt) <= now) return 'invite_expired';
  return 'invited';
}

export const portalInviteInput = z.object({
  /** Vazio = pelo país do fornecedor. */
  language: z.enum(PORTAL_LANGUAGES).optional(),
});

export const portalRevokeInput = z.object({
  reason: z.string().trim().min(3, 'Explique o motivo (pelo menos 3 caracteres)').max(1000),
});

export const portalAccessListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  status: z.enum(['active', 'invited', 'invite_expired', 'revoked']).optional(),
});
